import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import AuthImage from "@/components/AuthImage";
import { Plus, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function MyBoats() {
  const { t } = useLang();
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/boats").then((r) => setBoats(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm(t.boats.confirmDelete)) return;
    await api.delete(`/boats/${id}`);
    toast.success(t.boats.deleted);
    load();
  };

  const statusColor = { active: "bg-emerald-100 text-emerald-800", draft: "bg-slate-100 text-slate-600", reserved: "bg-amber-100 text-amber-800", sold: "bg-slate-200 text-slate-500", inactive: "bg-slate-100 text-slate-500" };
  const listingColor = { published: "bg-emerald-100 text-emerald-800", pending_review: "bg-amber-100 text-amber-800", rejected: "bg-red-100 text-red-700", unlisted: "bg-slate-100 text-slate-500" };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Bevela</p>
          <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-[#0B192C]">{t.boats.title}</h1>
        </div>
        <Link to="/mis-embarcaciones/nueva" className="btn-primary" data-testid="my-boats-add">
          <Plus className="h-4 w-4" /> {t.boats.add}
        </Link>
      </div>

      {loading ? (
        <p className="mt-12 text-slate-400">·</p>
      ) : boats.length === 0 ? (
        <div className="mt-16 border border-dashed border-slate-200 rounded-2xl p-12 text-center">
          <p className="text-slate-500">{t.dashboard.noBoats}</p>
          <Link to="/mis-embarcaciones/nueva" className="btn-primary mt-6 inline-flex" data-testid="my-boats-empty-add">
            <Plus className="h-4 w-4" /> {t.boats.add}
          </Link>
        </div>
      ) : (
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="my-boats-list">
          {boats.map((b) => {
            const primary = b.images?.find((i) => i.is_primary) || b.images?.[0];
            return (
              <div key={b.id} className="group" data-testid={`boat-card-${b.id}`}>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100">
                  {primary ? <AuthImage path={primary.storage_path} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> :
                    <div className="w-full h-full grid place-items-center text-slate-400 text-xs font-display">— no photo —</div>}
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-serif text-xl text-[#0B192C] truncate">{b.brand} {b.model}</h3>
                    <p className="text-xs text-slate-500 mt-1">{b.year} · {b.length ? `${b.length} m` : ""} · {b.location}</p>
                  </div>
                  <span className={`text-[10px] font-display uppercase tracking-widest px-2 py-1 rounded-full ${listingColor[b.listing_status] || "bg-slate-100 text-slate-500"}`} data-testid={`boat-listing-${b.id}`}>
                    {t.boats.listing[b.listing_status] || t.boats.listing.unlisted}
                  </span>
                </div>
                <div className="mt-3 flex items-baseline justify-between border-t border-slate-100 pt-3">
                  <span className="text-xs text-slate-500">{t.boats.ownership.sharePrice}</span>
                  <span className="font-serif text-lg text-[#0B192C]">€{Number(b.ownership?.standard_share_price || 0).toLocaleString()}</span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Link to={`/embarcacion/${b.id}`} className="btn-outline flex-1 text-center text-sm justify-center inline-flex" data-testid={`boat-view-${b.id}`}>
                    {t.boats.view}
                  </Link>
                  <Link to={`/mis-embarcaciones/${b.id}/editar`} className="btn-ghost text-sm inline-flex items-center gap-1" data-testid={`boat-edit-${b.id}`}>
                    <Edit3 className="h-3.5 w-3.5" /> {t.boats.edit}
                  </Link>
                  <button onClick={() => onDelete(b.id)} className="btn-ghost text-sm text-red-500 hover:text-red-700 inline-flex items-center gap-1" data-testid={`boat-delete-${b.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
